import { Component, HostListener, inject, OnDestroy } from '@angular/core';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable
} from '@angular/fire/storage';
import { FormArray, FormGroup } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { filter, lastValueFrom, map, Subscription } from 'rxjs';
import { FileControllerService } from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { ImageService } from 'src/app/services/image.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import {
  isSvgUploadFile,
  shouldCompressUpload,
  shouldValidateUploadDimensions
} from 'src/app/utils/image-upload.util';
import { TableUtil } from '../table/table.util';

@Component({
  selector: 'app-image-upload',
  template: '',
  standalone: true
})
export abstract class ImageUploadComponent<T> implements OnDestroy {
  protected imageService = inject(ImageService);
  protected storage = inject(Storage);

  filesUploaded: Array<string> = [];
  form: FormGroup | FormArray | undefined;
  subscription: Subscription;
  imagesReplaced: Array<string> = [];
  deleting = false;

  private extensionByMimeType: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'image/svg': 'svg'
  };

  constructor(
    protected fileService: FileControllerService,
    protected router: Router
  ) {
    this.subscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event) => {
        if (
          !(event as NavigationStart).url.includes(this.currentUrl) &&
          this.filesUploaded.length
        ) {
          this.deleteUploadedInList(this.filesUploaded).then(() => {
            console.log('Files deleted');
            this.filesUploaded = [];
          });
        }
      });
  }

  @HostListener('window:beforeunload')
  beforeUnload() {
    // Deleta os arquivos que foram enviados para o storage e não foram atualizados
    if (this.deleteOnUnload && this.filesUploaded.length)
      this.deleteUploadedInList(this.filesUploaded).then(() => {
        console.log('Files deleted');
        this.filesUploaded = [];
      });
  }

  ngOnDestroy(): void {
    // Deleta os arquivos que foram enviados para o storage e não foram atualizados
    if (this.filesUploaded.length)
      this.deleteUploadedInList(this.filesUploaded).then(() => {
        console.log('Files deleted');
        this.filesUploaded = [];
      });
  }

  async deleteUploadedInList(list: Array<string>): Promise<void> {
    if (!this.deleting)
      try {
        this.deleting = true;
        const results = await Promise.all(
          list.map((f) =>
            lastValueFrom(
              this.fileService.deleteFile(f).pipe(map((data) => data.result))
            )
          )
        );
        results.forEach((r) => console.log(r));
      } catch (error: any) {
        console.error(error);
      } finally {
        this.deleting = false;
      }
  }

  async onUpload(
    $event: FileUploadHandlerEvent,
    field: string,
    fileUpload: FileUpload = null,
    fileName: string = undefined,
    compress = false,
    expectedWidth?: number,
    expectedHeight?: number,
    percentMargin?: number
  ): Promise<string> {
    LoaderService.showLoader();
    const file = $event.files[0];
    if (
      shouldValidateUploadDimensions(file) &&
      expectedWidth &&
      expectedHeight &&
      !(await this.imageService.validateImageDimensions(
        file,
        expectedWidth / expectedHeight,
        undefined,
        undefined,
        percentMargin,
        expectedWidth,
        expectedHeight
      ))
    ) {
      fileUpload?.clear();
      LoaderService.showLoader(false);
      return;
    }
    if (!fileName && this.modelId) {
      fileName = this.modelId.toString();
    } else if (!fileName && this.model) {
      fileName = typeof this.model;
    } else if (!fileName) {
      fileName = new Date().getTime().toString();
    }

    fileName = this.normalizeUploadFileName(fileName, field, file);
    const fileWithNormalizedName = this.cloneFileWithName(file, fileName);

    let result: string;
    try {
      if (fileWithNormalizedName.size >= 32 * 1024 * 1000) {
        result = await this.uploadToBucket(
          this.filePath,
          fileWithNormalizedName,
          fileName
        );
      } else if (shouldCompressUpload(fileWithNormalizedName, compress)) {
        result = (await lastValueFrom(
          this.fileService
            .compressAndUploadFileForm(fileWithNormalizedName, this.filePath)
            .pipe(map((data) => data.result))
        )) as string;
      } else {
        // Se o arquivo tem menos de 32MB
        result = (await lastValueFrom(
          this.fileService
            .uploadFileForm(this.filePath, fileWithNormalizedName, fileName)
            .pipe(map((data) => data.result))
        )) as string;
      }
      this.filesUploaded.push(result);
      if (this.model && TableUtil.deepGet(this.model, field.split('.')))
        this.imagesReplaced.push(
          TableUtil.deepGet(this.model, field.split('.'))
        );
      this.form?.get(field)?.setValue(result);
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
    fileUpload?.clear();
    LoaderService.showLoader(false);
    return result;
  }

  private normalizeUploadFileName(
    baseFileName: string,
    field: string,
    file: File
  ): string {
    const hasExtension = baseFileName.includes('.');
    if (hasExtension) return baseFileName;

    const fileExtension = this.getFileExtension(file);

    return `${baseFileName}_${field}_${Date.now()}.${fileExtension}`;
  }

  private getFileExtension(file: File): string {
    const fileName = file?.name || '';
    const lastDotIndex = fileName.lastIndexOf('.');
    const extensionFromName =
      lastDotIndex > -1 ? fileName.slice(lastDotIndex + 1).toLowerCase() : '';

    if (extensionFromName) return extensionFromName;

    return this.extensionByMimeType[file?.type] || 'webp';
  }

  private cloneFileWithName(file: File, newFileName: string): File {
    const type = isSvgUploadFile(file)
      ? 'image/svg+xml'
      : file.type || 'application/octet-stream';

    if (file.name === newFileName && file.type === type) return file;

    return new File([file], newFileName, {
      type,
      lastModified: file.lastModified
    });
  }

  async uploadToBucket(filePath: string, file: File, fileName: string) {
    const storageRef = ref(this.storage, filePath.concat(fileName));
    // return await new Promise<string>((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed', (snapshot) => {
      AppDialogService.showProgressBarDialog(
        Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        'Fazendo upload'
      );
    });
    LoaderService.showLoader(false);
    await task.then();
    LoaderService.showLoader();
    return await getDownloadURL(storageRef);
    // });
  }

  async afterSubmit(): Promise<void> {
    if (this.imagesReplaced?.length) {
      await this.deleteUploadedInList(this.imagesReplaced);
      this.imagesReplaced = [];
    }
    const images = FormUtil.getImageLinksInObject(this.form.value);
    this.filesUploaded = this.filesUploaded.filter((f) => !images.includes(f));
    if (this.filesUploaded?.length) {
      await this.deleteUploadedInList(this.filesUploaded);
      this.filesUploaded = [];
    }
  }

  get deleteOnUnload(): boolean {
    return true;
  }

  get currentUrl(): string {
    return location.pathname;
  }

  public abstract get filePath(): string;
  public abstract get model(): T | undefined;
  public abstract get modelId(): string | number | undefined;
}
